import { NgModule } from '@angular/core';
import { provideConfig } from '../../../core/config/config-providers';

@NgModule({
  providers: [
    provideConfig({
      api: {
        endpoints: {
          // Admin Dashboard endpoints
          adminSessionHistory: 'admin/history/sessions',
          adminAuditHistory: 'admin/history/audit',
          adminActiveUsers: 'admin/history/active-users',
          adminOpenBatches: 'admin/batches/open',
          adminRecentTransactions: 'admin/transactions/recent',
          adminAchIngestions: 'admin/ach/ingestions',
          adminAchIngestionSummary: 'admin/ach/ingestions/summary',
          adminAchIngestionRequeue: 'admin/ach/ingestions/${ingestionId}/requeue',
          adminAchIngestionJob: 'admin/ach/ingestion-job',
          adminAchIngestionJobSetup: 'admin/ach/ingestion-job/setup',
          // Paya ACH ingestion processing settings + repair actions (SYSTEM_ADMIN).
          adminAchSettings: 'admin/ach/settings',
          adminAchPromoteReturns: 'admin/ach/promote-returns',
          adminAchRebuildCurrentState: 'admin/ach/rebuild-current-state',
          adminProfiles: 'admin/profiles',
          // Platform-wide email audit (SYSTEM_ADMIN). Distinct from
          // `adminProfileTestEmails`, which is profile-scoped and test-mode only.
          emailAuditList: 'admin/history/emails',
          // Agent-desktop uploaded logs (SYSTEM_ADMIN reads) and a profile's agent-desktop config.
          // Also declared in the other config module: the Audit drill-down (sessions-tab) is mounted
          // on both the admin dashboard and System Admin → Security.
          agentLogList: 'agent-logs',
          agentLogDetail: 'agent-logs/${logId}',
          agentLogDownload: 'agent-logs/${logId}/download',
          profileAgentDesktop: 'admin/profile/${profileId}/agent-desktop',
          emailAuditDetail: 'admin/history/emails/${emailAuditId}',
          emailAuditAttachment: 'admin/history/emails/${emailAuditId}/attachments/${attachmentId}',
          emailAuditForward: 'admin/history/emails/${emailAuditId}/forward',
          // Platform-wide notification log. Both ids are in the detail path
          // because Notifications has a composite primary key.
          notificationAuditList: 'system-admin/notifications/audit',
          notificationAuditProfiles: 'system-admin/notifications/audit/profiles',
          notificationAuditDetail: 'system-admin/notifications/audit/${profileId}/${notificationId}',
          // Core file store (FTEFileSys), across profiles. `profileId` is the literal `null` for
          // files with no profile — in the path segment and the query parameter alike.
          fileSysFiles: 'system/filesys/files',
          fileSysStats: 'system/filesys/stats',
          fileSysFile: 'system/filesys/profile/${profileId}/file/${fileId}',
          fileSysFileContent: 'system/filesys/profile/${profileId}/file/${fileId}/content',
          fileSysVersions: 'system/filesys/profile/${profileId}/versions',
          // Chunked uploads: start, send each part, complete. Parts exist because deployed FTE
          // runs on Cloud Run, which refuses a request body over 32 MiB.
          fileSysUploads: 'system/filesys/uploads',
          fileSysUpload: 'system/filesys/uploads/${uploadId}',
          // `headers: {}` drops ApiService's default `Content-Type: application/json`: the body is
          // FormData, and the browser must write `multipart/form-data; boundary=…` itself. With the
          // JSON header FTE's express.json() parses the binary part and every upload fails with a
          // 400 "SyntaxError" before reaching the route. Same arrangement as `erpImportRun`.
          fileSysUploadPart: { path: 'system/filesys/uploads/${uploadId}/parts/${partNumber}', headers: {} },
          fileSysUploadComplete: 'system/filesys/uploads/${uploadId}/complete',
          // Downloadable Windows builds and where each is kept in the file store.
          artifactProducts: 'artifacts',
          adminProfile: 'admin/profile/${profileId}',
          adminProfileCreate: 'admin/profile',
          adminProfileTemplate: 'admin/profile/<%= profileId %>/template',
          adminProfileTemplates: 'admin/profile-templates',
          // Staged import: dry run, then one section per wizard step, plus
          // apply-to-an-existing-profile.
          adminProfileTemplatePreview: 'admin/profile-templates/preview',
          adminProfileTemplateSection: 'admin/profile-templates/<%= profileId %>/sections/<%= section %>',
          adminProfileTemplateApply: 'admin/profile-templates/<%= profileId %>',
          adminUsers: 'admin/users',
          adminUser: 'admin/user/${userNumber}',
          adminUserProfiles: 'admin/user/${userNumber}/profiles',
          adminProfileUser: 'admin/profile/${profileId}/user/${userNumber}',
          testEmails: 'test/emails',
          testSendEmail: 'test/emails/send',
          testDateOverride: 'test/date-override',
          testInvoice: 'test/invoice',
          testMockHistory: 'test/mock/history',
          testMockCustomers: 'test/mock/customers',
          testMockCustomersSample: 'test/mock/customers/sample',
          systemEnvironment: 'systems/environment',

          // Admin module endpoints
          profile: 'admin/profile/${profileId}',
          profiles: 'admin/profiles',
          profileTemplate: 'admin/profile/<%= profileId %>/template',
          profileTemplates: 'admin/profile-templates',
          users: 'admin/users',
          newuser: 'admin/user',
          user: 'admin/user/${userNumber}',
          profileUsers: 'admin/profile/${profileId}/users',
          profileUser: 'admin/profile/${profileId}/user/${userNumber}',
          roles: 'admin/roles/${profile}',
          currentUserRoles: 'self/profile/${profileId}/roles',
          resetPassword: 'admin/user/${userNumber}/resetpassword',
          changeSelfPassword: 'self/changePassword',
          profileLogo: 'admin/profile/${profileId}/logo',
          uploadProfileLogo: 'admin/profile/${profileId}/logo',
          deleteProfileLogo: 'admin/profile/${profileId}/logo',
          systemGateways: 'systems/gateways',
          systemBins: 'systems/bins',
          systemBinCreate: 'systems/bin',
          systemBin: 'systems/bin/${name}',
          systemBinLookup: 'systems/lookup/bin/${number}',
          systemImpersonate: 'systems/security/impersonate',
          profileApiKey: 'admin/profile/${profileId}/keys/${keyName}',
          validateApiKey: 'admin/user/${profileId}/keys/${keyName}/validate',
          deleteApiKey: 'admin/user/${profileId}/keys/${keyName}',
          schedulerJobs: 'scheduler/jobs',
          schedulerJob: 'scheduler/jobs/${jobId}',
          schedulerJobRun: 'scheduler/jobs/${jobId}/run',
          schedulerJobReset: 'scheduler/jobs/${jobId}/reset',
          schedulerJobEnable: 'scheduler/jobs/${jobId}/enable',
          schedulerJobDisable: 'scheduler/jobs/${jobId}/disable',
          schedulerJobHistory: 'scheduler/jobs/${jobId}/history',
          schedulerHistory: 'scheduler/history',
          schedulerCommands: 'scheduler/commands',
          schedulerCommandGroup: 'scheduler/commands/${groupName}',

          // Payment Frame endpoints
          paymentFrames: 'admin/profile/${profileId}/paymentFrames',
          paymentFrame: 'admin/profile/${profileId}/paymentFrame/${paymentFrameId}',
          paymentFrameCreate: 'admin/profile/${profileId}/paymentFrame',

          // Admin profile tokens
          adminProfileTokens: 'admin/profile/${profileId}/tokens',

          // System-admin destructive: full profile purge as a background scheduler job
          adminStartDeleteProfileJob: 'admin/profile/${profileId}/delete-job',
          adminGetDeleteProfileJob: 'admin/jobs/${jobId}',
          // System-admin destructive: truncate profile data (reset to "square one")
          adminStartTruncateProfileJob: 'admin/profile/${profileId}/truncate-job',
          adminGetTruncateProfileJob: 'admin/truncate-jobs/${jobId}',
          // System-admin destructive: purge ERP data, keeping Freely payments, so history can be reimported
          adminPreviewErpPurge: 'admin/profile/${profileId}/erp-purge/preview',
          adminStartErpPurgeJob: 'admin/profile/${profileId}/erp-purge-job',
          adminGetErpPurgeJob: 'admin/erp-purge-jobs/${jobId}',
          // Payment transfer: export a profile's Freely payments / import them into
          // this environment, both as background jobs (Maintenance tab).
          adminStartPaymentExportJob: 'admin/profile/${profileId}/payment-export-job',
          adminPreviewPaymentImport: 'admin/profile/${profileId}/payment-import/preview',
          adminStartPaymentImportJob: 'admin/profile/${profileId}/payment-import-job',
          adminGetPaymentTransferJob: 'admin/payment-transfer-jobs/${jobId}',
          // Customer ledger: solve every customer's opening balance forward as a
          // background job. Not destructive, and not non-prod only.
          startBalanceForwardJob: 'customer-ledger/balance-forward-job',
          getBalanceForwardJob: 'customer-ledger/balance-forward-jobs/${jobId}',
          customerLedgerResetStatus: 'customer-ledger/reset',
          startCustomerLedgerResetJob: 'customer-ledger/reset-job',
          getCustomerLedgerResetJob: 'customer-ledger/reset-jobs/${jobId}',
          adminProfileTimeZone: 'admin/profile/${profileId}/timezone',
          adminProfileAutopaySettings:
            'admin/profile/${profileId}/autopay/settings',
          adminProfileAutopayTermRules:
            'admin/profile/${profileId}/autopay/term-rules',
          adminProfileAutopayEnrollmentPreview:
            'admin/profile/${profileId}/autopay/enrollment-preview',
          adminProfileAutopayMockTokens:
            'admin/profile/${profileId}/autopay/mock-tokens',
          adminProfileAutopayRecalcPreview:
            'admin/profile/${profileId}/autopay/recalc-preview',
          adminProfileAutopayRecalcJob:
            'admin/profile/${profileId}/autopay/recalc-job',
          adminGetAutopayRecalcJob: 'admin/autopay-recalc-jobs/${jobId}',
          adminProfileAutopayEnrollmentApply:
            'admin/profile/${profileId}/autopay/enrollment-apply',
          adminProfileAutopayExceptions:
            'admin/profile/${profileId}/autopay/exceptions',
          adminProfileAutopayRun: 'admin/profile/${profileId}/autopay/run',
          adminProfileLookupValues:
            'admin/profile/${profileId}/lookups/${valueType}',

          // Customer endpoints (for mock data generation)
          customersListCustomer: 'customers',
          customersCreateCustomer: 'customer',
        },
      },
    }),
  ],
})
export class SystemAdminConfigModule {}
